// O DONO DA TECLA OK — para superfícies de escape que NÃO vivem na árvore do
// Norigin.
//
// ===== O DEFEITO QUE ESTE MÓDULO EXISTE PRA MATAR =====
// Um `<button>` HTML nativo focado responde a Enter porque o navegador executa
// a *activation behavior* como AÇÃO PADRÃO do `keydown`. O Norigin cancela essa
// ação padrão para TODA tecla do keymap — setas E Enter — antes de qualquer
// decisão sua:
//
//   if (!this.shouldUseNativeEvents) { event.preventDefault(); event.stopPropagation() }
//   ...
//   if (eventType === KEY_ENTER && this.focusKey) { this.onEnterPress(...); return }
//
// (@noriginmedia/norigin-spatial-navigation-core, bindEventHandlers)
//
// Repare que o `preventDefault()` vem ANTES do `if`: não importa se há foco
// lógico, se a árvore está vazia ou se o app acabou de crashar. O `init()` do
// app (bootstrap.ts) não passa `shouldUseNativeEvents`, cujo default é `false`,
// então enquanto o Norigin estiver vivo e não pausado o Enter NUNCA vira click
// nativo. O botão aparece, aparece FOCADO, e o OK do controle não faz nada.
// Numa TV isso é a pessoa presa: não há mouse pra contornar.
//
// ===== POR QUE CAPTURE NO WINDOW, E NÃO `onKeyDown` NO BOTÃO =====
// O listener do Norigin está no `window` em fase de BOLHA. A fase de CAPTURA do
// `window` é o PRIMEIRO passo do despacho de qualquer evento, independente da
// ordem de registro — então este dono roda antes, decide, e `stopPropagation()`
// impede que o Norigin ainda chame `onEnterPress` numa `focusKey` obsoleta (que
// no caso de um overlay modal chegaria a ATIVAR um card ATRÁS do overlay, pior
// que não fazer nada). Um `onKeyDown` no JSX também rodaria antes do Norigin
// (React escuta no container raiz), mas só se o evento chegar até o elemento —
// e não impede o `onEnterPress` fantasma.
//
// ===== POR QUE NÃO `pause()` DO NORIGIN =====
// `pause()`/`resume()` do Norigin são um FLAG global, não um contador. As telas
// de player já pausam o spatial nav; um overlay que pausasse e desse `resume()`
// ao desmontar devolveria a navegação para uma tela que a queria pausada. Este
// módulo não toca estado de ninguém: instala e remove o próprio listener.
//
// USO: superfície com UMA ação principal e nenhuma árvore de foco do Norigin
// (tela de crash, overlay modal de 1 botão). NÃO use em tela navegável — ali o
// OK é do Norigin, e o certo é `Focusable`/`onEnterPress`.

import { useEffect, useRef } from 'react'

/** Subconjunto estrutural do KeyboardEvent que a decisão precisa — deixa a
 *  função pura testável sem DOM. */
export interface OkKeyEventLike {
  key: string
  keyCode: number
}

// `key` e `keyCode` juntos de propósito: WebViews de TV 2017 divergem. Tizen e
// webOS mandam 13/'Enter' no OK do controle; alguns emitem ' '/32 (Espaço) em
// teclado virtual/USB. O mesmo par que `TrialOverlay.isNavOrActionKey` já usa.
const OK_KEYS = new Set<string>(['Enter', ' ', 'Spacebar'])
const OK_KEYCODES = new Set<number>([13, 32])

/** Decisão PURA: este keydown é o "OK/ativar" do controle? */
export function isOkKeyEvent(e: OkKeyEventLike): boolean {
  return OK_KEYS.has(e.key) || OK_KEYCODES.has(e.keyCode)
}

/**
 * Faz desta superfície a DONA da tecla OK enquanto `enabled` for true.
 *
 * @param onActivate ação única da superfície (recarregar, voltar…)
 * @param enabled    desliga sem desmontar o componente
 */
export function useOkKeyOwner(onActivate: () => void, enabled = true): void {
  // Ref pra estabilizar o handler: callers passam arrow inline, e sem isto o
  // listener seria removido/readicionado a cada render — janela de corrida onde
  // justamente o OK do usuário passa despercebido. Mesmo padrão do useBackKey.
  const handlerRef = useRef(onActivate)
  handlerRef.current = onActivate

  useEffect(() => {
    if (!enabled) return

    const onKey = (e: KeyboardEvent): void => {
      if (!isOkKeyEvent(e)) return
      // `preventDefault` aqui é o que impede a DUPLA ativação quando o Norigin
      // está pausado (aí a ação padrão nativa ainda existiria).
      e.preventDefault()
      e.stopPropagation()
      handlerRef.current()
    }

    // `true` e não `{ capture: true }`: o objeto de options é Chr49+, e o piso
    // do produto é Chr38. Lá o objeto seria coagido pra booleano (truthy → vira
    // capture do mesmo jeito), mas depender de coerção pra acertar a FASE do
    // listener de um botão de escape é frágil demais pro que está em jogo.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [enabled])
}
